import React, { Component } from 'react';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import { find } from 'lodash';
import ButtonInc from '../../../../../shared-ui/ButtonInc';
import PlanSelectionForm from './Components/PlanSelectionForm';
import MemberComparisonTable from './Components/MemberComparisonTable';
import SummaryScreen from './Components/SummaryScreen';
import AmendmentProgressDialog from '../shared/Components/AmendmentProgressDialog';
import PolicyInfoHeader from '../shared/Components/PolicyInfoHeader';
import AmendmentPageHeader from '../shared/Components/AmendmentPageHeader';
import { extractMembersFromPolicy, groupMembers, getPlanNumber, validateParentPlan } from './utils/planHelpers';
import { calculateNewPremiums, buildChangePlanQuotation } from './utils/premiumCalculator';
import { processChangePlanAmendment } from './utils/amendmentApi';
import { calculatePremiumChange } from './utils/calculations';
import { getPartner } from '../../../../../lib/access.es6';
import { getAllProducts } from '../../../../../lib/requests.es6';

class ChangePlanScreen extends Component {
  constructor(props) {
    super(props);

    const { policy, products } = props;
    const allMembers = extractMembersFromPolicy(policy);

    // Get current plan codes from policy
    const currentNuclearPlanCode = policy.quotation && policy.quotation.additional_details ? policy.quotation.additional_details.primary_plan : null;
    const currentParentPlanCode = policy.quotation && policy.quotation.additional_details ? policy.quotation.additional_details.secondary_plan : null;

    this.state = {
      allMembers,
      originalMembers: allMembers,
      updatedMembers: allMembers,
      droppedBenefits: [],
      currentNuclearPlanCode,
      currentParentPlanCode,
      selectedNuclearPlanCode: '',
      selectedParentPlanCode: '',
      products: products || [],
      loadingProducts: false,
      // Amendment progress tracking
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSummary: false,
      showSuccessDialog: false
    };
  }

  componentDidMount() {
    const { products } = this.props;

    // Fetch products if not available from Redux
    if (!products || products.length === 0) {
      this.fetchProducts();
    }
  }

  fetchProducts = async () => {
    this.setState({ loadingProducts: true });

    try {
      const response = await getAllProducts();
      const products = response.data || [];

      this.setState({
        products,
        loadingProducts: false
      }, () => {
        // Recalculate premiums if both plans are already selected
        const { selectedNuclearPlanCode, selectedParentPlanCode } = this.state;
        if (selectedNuclearPlanCode && selectedParentPlanCode) {
          this.recalculatePremiums();
        }
      });
    } catch (error) {
      console.error('Failed to fetch products:', error);
      this.setState({
        loadingProducts: false
      });
    }
  }

  handleNuclearPlanChange = (planCode) => {
    const { selectedParentPlanCode } = this.state;

    // Validate parent plan against new nuclear plan
    const validation = validateParentPlan(planCode, selectedParentPlanCode);

    if (!validation.isValid) {
      // Reset parent plan if it's invalid
      this.setState({
        selectedNuclearPlanCode: planCode,
        selectedParentPlanCode: ''
      }, () => {
        this.recalculatePremiums();
      });
    } else {
      this.setState({
        selectedNuclearPlanCode: planCode
      }, () => {
        this.recalculatePremiums();
      });
    }
  };

  handleParentPlanChange = (planCode) => {
    this.setState({
      selectedParentPlanCode: planCode
    }, () => {
      this.recalculatePremiums();
    });
  };

  recalculatePremiums = () => {
    const { policy } = this.props;
    const { selectedNuclearPlanCode, selectedParentPlanCode, allMembers, products } = this.state;

    console.log('=== RECALCULATE PREMIUMS ===');
    console.log('Selected Nuclear Plan Code:', selectedNuclearPlanCode);
    console.log('Selected Parent Plan Code:', selectedParentPlanCode);

    if (!selectedNuclearPlanCode || !selectedParentPlanCode) {
      console.log('Waiting for both plans to be selected');
      return; // Wait for both plans to be selected
    }

    if (!products || products.length === 0) {
      console.error('Products not loaded yet');
      return;
    }

    console.log('Total products available:', products.length);
    console.log('Product codes:', products.map(p => p.code));

    // Find plan objects from products
    const newNuclearPlan = find(products, { code: selectedNuclearPlanCode });
    const newParentPlan = find(products, { code: selectedParentPlanCode });

    console.log('Found Nuclear Plan:', newNuclearPlan ? newNuclearPlan.name : 'NOT FOUND');
    console.log('Found Parent Plan:', newParentPlan ? newParentPlan.name : 'NOT FOUND');

    if (!newNuclearPlan || !newParentPlan) {
      console.error('Plans not found:', selectedNuclearPlanCode, selectedParentPlanCode);
      console.log('Available products:', products);
      return;
    }

    console.log('Nuclear Plan Config:', newNuclearPlan.membership_configs);
    console.log('Parent Plan Config:', newParentPlan.membership_configs);

    // Calculate new premiums for all members
    const result = calculateNewPremiums(allMembers, newNuclearPlan, newParentPlan, policy);

    console.log('Original members premiums:', allMembers.map(m => ({ name: m.first_name, premium: m.premium_amount_in_cents })));
    console.log('Updated members premiums:', result.updatedMembers.map(m => ({ name: m.first_name, premium: m.premium_amount_in_cents })));
    console.log('Dropped benefits:', result.droppedBenefits);

    this.setState({
      updatedMembers: result.updatedMembers,
      droppedBenefits: result.droppedBenefits
    });
  };

  handleNext = () => {
    this.setState({ showSummary: true });
  };

  handleBackFromSummary = () => {
    this.setState({ showSummary: false });
  };

  handleConfirmPlanChange = async () => {
    const { policy } = this.props;
    const { updatedMembers, selectedNuclearPlanCode, selectedParentPlanCode } = this.state;

    // Reset step statuses
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false
    });

    try {
      // Build quotation payload
      const quotationPayload = buildChangePlanQuotation(
        policy,
        updatedMembers,
        selectedNuclearPlanCode,
        selectedParentPlanCode
      );

      // Process the amendment with status callbacks
      const updateStatus = (step, status) => {
        // Update step status (loading, success, error)
        this.setState(prevState => ({
          steps: { ...prevState.steps, [step]: status }
        }));
      };

      const result = await processChangePlanAmendment(
        quotationPayload,
        policy,
        selectedNuclearPlanCode,
        selectedParentPlanCode,
        updateStatus
      );

      // Amendment succeeded (even if note failed)
      this.setState({
        showSuccessDialog: true,
        noteError: result.noteError || null
      });
    } catch (error) {
      console.error('Failed to process plan change:', error);
      const errorMessage = error.message || 'Failed to process plan change. Please try again.';

      // Determine which step failed
      this.setState(prevState => {
        const quoteError = prevState.steps.quote === 'error' ? errorMessage : null;
        const swapError = prevState.steps.swap === 'error' ? errorMessage : null;

        return { quoteError, swapError };
      });
    }
  };

  handleSuccess = () => {
    const { policy } = this.props;
    this.setState({ showSuccessDialog: false });
    // Navigate to policy processing page
    hashHistory.push(`admin/policy-processing/${policy.policy_number}`);
  };

  handleRetry = () => {
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null
    });
  };

  handleCancel = () => {
    if (this.props.onBack) {
      this.props.onBack();
    }
  };

  renderSummaryScreen() {
    const {
      currentNuclearPlanCode,
      currentParentPlanCode,
      selectedNuclearPlanCode,
      selectedParentPlanCode,
      steps,
      quoteError,
      swapError,
      noteError
    } = this.state;

    return (
      <div>
        <SummaryScreen
          policy={this.props.policy}
          originalMembers={this.state.originalMembers}
          updatedMembers={this.state.updatedMembers}
          oldNuclearPlanCode={currentNuclearPlanCode}
          oldParentPlanCode={currentParentPlanCode}
          newNuclearPlanCode={selectedNuclearPlanCode}
          newParentPlanCode={selectedParentPlanCode}
          onBack={this.handleBackFromSummary}
          onConfirm={this.handleConfirmPlanChange}
        />

        <AmendmentProgressDialog
          steps={steps}
          quoteError={quoteError}
          swapError={swapError}
          noteError={noteError}
          successMessage={`The policy plan has been successfully changed. Nuclear Plan: Plan ${getPlanNumber(selectedNuclearPlanCode)} | Parent Plan: Plan ${getPlanNumber(selectedParentPlanCode)}`}
          onViewPolicy={this.handleSuccess}
          onRetry={this.handleRetry}
        />
      </div>
    );
  }

  render() {
    const { policy } = this.props;
    const {
      currentNuclearPlanCode,
      currentParentPlanCode,
      selectedNuclearPlanCode,
      selectedParentPlanCode,
      originalMembers,
      updatedMembers,
      droppedBenefits,
      loadingProducts,
      steps,
      quoteError,
      swapError,
      noteError,
      showSummary
    } = this.state;

    const currencyCode = getPartner().country.currency_code;

    // Calculate premium change
    const premiumChange = calculatePremiumChange(originalMembers, updatedMembers);

    // Check if plans have changed (only if user has made a selection)
    const plansChanged =
      (selectedNuclearPlanCode && selectedNuclearPlanCode !== currentNuclearPlanCode) ||
      (selectedParentPlanCode && selectedParentPlanCode !== currentParentPlanCode);

    // Check if both plans are selected
    const bothPlansSelected = selectedNuclearPlanCode && selectedParentPlanCode;

    // Check if amendment is in progress
    const isProcessing = steps.quote === 'loading' || steps.swap === 'loading' || steps.note === 'loading';

    // Validate parent plan
    const validation = validateParentPlan(selectedNuclearPlanCode, selectedParentPlanCode);

    const canProceed = plansChanged && bothPlansSelected && validation.isValid;

    // Group members for display
    const groupedOriginal = groupMembers(originalMembers);
    const groupedUpdated = groupMembers(updatedMembers);

    // Show summary screen if in summary mode
    if (showSummary) {
      return this.renderSummaryScreen();
    }

    // Show loading indicator while products are being fetched
    if (loadingProducts) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>Loading plan information...</h3>
          <p>Please wait while we fetch available plans.</p>
        </div>
      );
    }

    return (
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Unified Header */}
        <AmendmentPageHeader
          title="Change Plan"
          subtitle="Select new plans and review premium changes before confirming."
          onBack={this.handleCancel}
        />

        {/* Policy Information */}
        <PolicyInfoHeader policy={policy} />

        {/* Plan Selection Form */}
        <PlanSelectionForm
          currentNuclearPlan={currentNuclearPlanCode}
          currentParentPlan={currentParentPlanCode}
          selectedNuclearPlan={selectedNuclearPlanCode}
          selectedParentPlan={selectedParentPlanCode}
          onNuclearPlanChange={this.handleNuclearPlanChange}
          onParentPlanChange={this.handleParentPlanChange}
          plansChanged={plansChanged}
          availablePlans={null} // Will use default Plan 1-7
        />

        {/* Premium Summary (if plans selected) */}
        {bothPlansSelected && validation.isValid && (
          <div style={{
            backgroundColor: premiumChange.isIncrease ? '#e8f5e9' : (premiumChange.isDecrease ? '#ffebee' : '#f5f5f5'),
            border: `1px solid ${premiumChange.isIncrease ? '#a5d6a7' : (premiumChange.isDecrease ? '#ef9a9a' : '#ddd')}`,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Current Total Premium:</span>
                <span style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '10px' }}>
                  {currencyCode} {(premiumChange.oldTotal / 100).toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#666' }}>→</div>
              <div>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>New Total Premium:</span>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginLeft: '10px',
                  color: premiumChange.isIncrease ? '#388e3c' : (premiumChange.isDecrease ? '#d32f2f' : '#000')
                }}>
                  {currencyCode} {(premiumChange.newTotal / 100).toFixed(2)}
                </span>
              </div>
            </div>
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: premiumChange.isIncrease ? '#388e3c' : (premiumChange.isDecrease ? '#d32f2f' : '#666')
              }}>
                {premiumChange.isIncrease && `Premium Increase: +${currencyCode} ${(Math.abs(premiumChange.difference) / 100).toFixed(2)}`}
                {premiumChange.isDecrease && `Premium Decrease: -${currencyCode} ${(Math.abs(premiumChange.difference) / 100).toFixed(2)}`}
                {!premiumChange.isIncrease && !premiumChange.isDecrease && 'No Premium Change'}
              </span>
            </div>
          </div>
        )}

        {/* Dropped Benefits Warning */}
        {droppedBenefits && droppedBenefits.length > 0 && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '6px',
            padding: '15px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            <h6 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px', color: '#856404' }}>
              <span role="img" aria-label="Warning">⚠️</span> Benefits Not Available in Selected Plan
            </h6>
            <p style={{ marginBottom: '10px', fontSize: '14px' }}>
              The following benefits are not available in the selected plan and will be removed:
            </p>
            <ul style={{ marginLeft: '20px', fontSize: '13px' }}>
              {droppedBenefits.map((dropped, index) => (
                <li key={index} style={{ marginBottom: '5px' }}>
                  <strong>{dropped.memberName}</strong> ({dropped.relationship}): {dropped.benefitType}
                  {dropped.premium > 0 && ` - Premium: ${currencyCode} ${(dropped.premium / 100).toFixed(2)}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Member Comparison Tables */}
        {bothPlansSelected && validation.isValid && (
          <>
            <h5 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: '#032A37' }}>
              Member Premium Comparison
            </h5>

            <MemberComparisonTable
              originalMembers={groupedOriginal.nuclear}
              updatedMembers={groupedUpdated.nuclear}
              title="Nuclear Members"
            />

            {groupedOriginal.parents.length > 0 && (
              <MemberComparisonTable
                originalMembers={groupedOriginal.parents}
                updatedMembers={groupedUpdated.parents}
                title="Parent Members"
              />
            )}

            {groupedOriginal.extended.length > 0 && (
              <MemberComparisonTable
                originalMembers={groupedOriginal.extended}
                updatedMembers={groupedUpdated.extended}
                title="Extended Members (Unchanged)"
              />
            )}
          </>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
          <ButtonInc
            onClick={this.handleNext}
            disabled={!canProceed || isProcessing}
            style={{
              background: !canProceed || isProcessing ? '#ccc' : '#032A37',
              opacity: !canProceed || isProcessing ? 0.6 : 1
            }}
          >
            Next
          </ButtonInc>
        </div>

        {/* Amendment Progress Dialog */}
        <AmendmentProgressDialog
          steps={steps}
          quoteError={quoteError}
          swapError={swapError}
          noteError={noteError}
          successMessage={`The policy plan has been successfully changed. Nuclear Plan: Plan ${getPlanNumber(selectedNuclearPlanCode)} | Parent Plan: Plan ${getPlanNumber(selectedParentPlanCode)}`}
          onViewPolicy={this.handleSuccess}
          onRetry={this.handleRetry}
        />
      </div>
    );
  }
}

export default connect((state) => ({
  products: state.customerScreen ? state.customerScreen.products : []
}))(ChangePlanScreen);
